import React, { useState, useEffect, useCallback } from 'react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { Sidebar, MobileHeader } from '../components/shared/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import axios from '../lib/axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Brain, Users, Clock, TrendingUp, TrendingDown, Zap, Target,
  Trophy, Medal, Crown, Shield, Flame, Star, AlertTriangle,
  ChevronRight, ArrowUp, ArrowDown, Activity, Bell, CheckCircle,
  XCircle, Timer, DollarSign, BarChart3, RefreshCw, X, Eye,
  Calendar, Sparkles, Sword, Heart
} from 'lucide-react';
import { formatCurrency, formatDateTime, formatRelativeTime } from '../lib/utils';
import { usePageFilter } from '../contexts/PageFilterContext';
import { PageFilterTabs } from '../components/shared/PageFilterTabs';

// Animated background particles
function GamingParticles() {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {[...Array(15)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 rounded-full"
          style={{
            background: ['#00F5FF', '#9945FF', '#FFD700', '#00FF88', '#FF3366'][i % 5],
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -20, 0],
            opacity: [0.2, 0.6, 0.2],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
        />
      ))}
    </div>
  );
}

// Animated number counter
function AnimatedNumber({ value, prefix = '', decimals = 0 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const end = parseFloat(value) || 0;
    const duration = 1500;
    const start = Date.now();
    const animate = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      setDisplay(end * (1 - Math.pow(1 - progress, 3)));
      if (progress < 1) requestAnimationFrame(animate);
    };
    animate();
  }, [value]);
  return <span className="font-mono tabular-nums">{prefix}{decimals > 0 ? display.toFixed(decimals) : Math.round(display)}</span>;
}

// Performance tier badge
function TierBadge({ tier, size = 'md' }) {
  const config = {
    HIGH: { icon: Crown, color: '#00FF88', bg: '#00FF8820', label: 'ELITE' },
    MEDIUM: { icon: Shield, color: '#FFD700', bg: '#FFD70020', label: 'WARRIOR' },
    LOW: { icon: Target, color: '#FF3366', bg: '#FF336620', label: 'ROOKIE' }
  };
  const { icon: Icon, color, bg, label } = config[tier] || config.MEDIUM;
  const sizeClasses = size === 'lg' ? 'px-4 py-2 text-sm' : 'px-2 py-1 text-xs';
  
  return (
    <motion.span 
      animate={{ boxShadow: [`0 0 10px ${color}40`, `0 0 20px ${color}60`, `0 0 10px ${color}40`] }}
      transition={{ repeat: Infinity, duration: 2 }}
      className={`inline-flex items-center gap-1.5 rounded-lg font-bold ${sizeClasses}`}
      style={{ backgroundColor: bg, color, border: `1px solid ${color}50` }}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </motion.span>
  );
}

export default function EmployeeIntelPage() {
  const { selectedPageId, dateRange } = usePageFilter();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [intel, setIntel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeDetail, setEmployeeDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const fetchIntel = useCallback(async () => {
    try {
      setRefreshing(true);
      const params = {};
      if (selectedPageId) params.page_id = selectedPageId;
      if (dateRange.startDate) params.start_date = dateRange.startDate;
      if (dateRange.endDate) params.end_date = dateRange.endDate;
      const response = await axios.get('/employee-intel', { params });
      setIntel(response.data);
    } catch (error) {
      toast.error('Failed to load employee intel');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedPageId, dateRange]);

  useEffect(() => {
    fetchIntel();
  }, [fetchIntel]);

  useAutoRefresh(fetchIntel, 20000);

  const fetchEmployeeDetail = async (employeeId) => {
    try {
      setLoadingDetail(true);
      const response = await axios.get(`/employee-intel/${employeeId}`);
      setEmployeeDetail(response.data);
    } catch (error) {
      toast.error('Failed to load employee details');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Peak hour detail modal state
  const [showPeakHourDetail, setShowPeakHourDetail] = useState(false);
  const [peakHourDetail, setPeakHourDetail] = useState(null);
  const [loadingPeakDetail, setLoadingPeakDetail] = useState(false);

  const fetchPeakHourDetail = async (hour) => {
    try {
      setLoadingPeakDetail(true);
      const response = await axios.get(`/peak-hour-details/${hour}`);
      setPeakHourDetail(response.data);
      setShowPeakHourDetail(true);
    } catch (error) {
      toast.error('Failed to load peak hour details');
    } finally {
      setLoadingPeakDetail(false);
    }
  };

  const handleSelectEmployee = (emp) => {
    setSelectedEmployee(emp);
    fetchEmployeeDetail(emp.employee_id);
  };

  const sendActivityCheck = async () => {
    try {
      const response = await axios.post('/activity-checks');
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to send activity check');
    }
  };

  const formatHour = (hour) => {
    const h = hour % 12 || 12;
    const ampm = hour < 12 ? 'AM' : 'PM';
    return `${h}${ampm}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#080810]">
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }} className="relative">
          <div className="w-20 h-20 rounded-full border-4 border-[#9945FF]/30 border-t-[#9945FF]" />
          <Brain className="absolute inset-0 m-auto w-8 h-8 text-[#9945FF]" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080810]" data-testid="employee-intel-page">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <MobileHeader onMenuClick={() => setSidebarOpen(true)} />

      {/* Background Effects */}
      <GamingParticles />
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#9945FF]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-[#00F5FF]/5 rounded-full blur-[150px]" />
      </div>

      <main className="lg:ml-72 min-h-screen relative z-10 p-4 md:p-6 lg:p-8">
        {/* Epic Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6"
        >
          <div className="flex items-center gap-4">
            <motion.div
              animate={{ 
                boxShadow: ['0 0 20px #9945FF', '0 0 40px #9945FF', '0 0 20px #9945FF'],
              }}
              transition={{ repeat: Infinity, duration: 3 }}
              className="p-3 rounded-xl bg-gradient-to-br from-[#9945FF]/20 to-[#00F5FF]/20 border border-[#9945FF]/30"
            >
              <Brain className="w-8 h-8 text-[#9945FF]" />
            </motion.div>
            <div>
              <h1 className="text-3xl md:text-4xl font-black text-white" style={{ textShadow: '0 0 30px rgba(153,69,255,0.3)' }}>
                INTEL CENTER
              </h1>
              <p className="text-gray-400">Employee Performance Analytics</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={sendActivityCheck}
              className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#FF3366] to-[#FFD700] text-black font-bold flex items-center gap-2"
              data-testid="send-check-btn"
            >
              <Bell className="w-4 h-4" /> PING ALL
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={fetchIntel}
              disabled={refreshing}
              className="px-4 py-2.5 rounded-xl bg-white/10 text-white font-bold flex items-center gap-2 hover:bg-white/20"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </motion.button>
          </div>
        </motion.div>

        {/* Page Filter */}
        <div className="mb-6">
          <PageFilterTabs />
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="bg-[#0d0d15] border-0 p-4 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#00F5FF] to-transparent" />
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-[#00F5FF]/10"><Users className="w-5 h-5 text-[#00F5FF]" /></div>
                <span className="text-gray-400 text-sm">TOTAL</span>
              </div>
              <p className="text-3xl font-black text-white" data-testid="total-employees">
                <AnimatedNumber value={intel?.summary?.total_employees || 0} />
              </p>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="bg-[#0d0d15] border-0 p-4 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#00FF88] to-transparent" />
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-[#00FF88]/10"><Crown className="w-5 h-5 text-[#00FF88]" /></div>
                <span className="text-gray-400 text-sm">ELITE</span>
              </div>
              <p className="text-3xl font-black text-[#00FF88]" data-testid="high-performers">
                <AnimatedNumber value={intel?.summary?.high_performers || 0} />
              </p>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="bg-[#0d0d15] border-0 p-4 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FFD700] to-transparent" />
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-[#FFD700]/10"><Shield className="w-5 h-5 text-[#FFD700]" /></div>
                <span className="text-gray-400 text-sm">WARRIORS</span>
              </div>
              <p className="text-3xl font-black text-[#FFD700]" data-testid="medium-performers">
                <AnimatedNumber value={intel?.summary?.medium_performers || 0} />
              </p>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
            <Card className="bg-[#0d0d15] border-0 p-4 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF3366] to-transparent" />
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-[#FF3366]/10"><Target className="w-5 h-5 text-[#FF3366]" /></div>
                <span className="text-gray-400 text-sm">ROOKIES</span>
              </div>
              <p className="text-3xl font-black text-[#FF3366]" data-testid="low-performers">
                <AnimatedNumber value={intel?.summary?.low_performers || 0} />
              </p>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="bg-[#0d0d15] border-0 p-4 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#9945FF] to-transparent" />
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-[#9945FF]/10"><Activity className="w-5 h-5 text-[#9945FF]" /></div>
                <span className="text-gray-400 text-sm">AVG SCORE</span>
              </div>
              <p className="text-3xl font-black text-[#9945FF]" data-testid="avg-score">
                <AnimatedNumber value={intel?.summary?.avg_performance_score || 0} decimals={1} />
              </p>
            </Card>
          </motion.div>
        </div>

        {/* Peak Hours */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="mb-6">
          <Card className="bg-[#0d0d15] border-0 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Flame className="w-5 h-5 text-[#FF6B00]" /> PEAK HOURS
              </h3>
              <span className="text-xs text-gray-500">Click to see who was active</span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {intel?.peak_hours?.map((hour, idx) => (
                <motion.div
                  key={hour.hour}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.05 * idx }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => fetchPeakHourDetail(hour.hour)}
                  className="min-w-[100px] p-3 rounded-xl bg-white/5 border border-white/10 text-center cursor-pointer hover:border-[#FF6B00]/50 transition-all"
                  data-testid={`peak-hour-${hour.hour}`}
                >
                  <p className="text-2xl font-black text-white">{formatHour(hour.hour)}</p>
                  <p className="text-xs text-gray-400">{hour.transactions} battles</p>
                  <p className="text-sm font-mono text-[#FFD700]">{formatCurrency(hour.volume)}</p>
                  <p className="text-[10px] text-gray-500 mt-1">Click for details</p>
                </motion.div>
              ))}
              {(!intel?.peak_hours || intel.peak_hours.length === 0) && (
                <div className="w-full text-center py-4 text-gray-500">No peak hour data yet</div>
              )}
            </div>
          </Card>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Employee Rankings */}
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }} className="lg:col-span-2">
            <Card className="bg-[#0d0d15] border-0 overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#9945FF] to-transparent" />
              <div className="p-4 border-b border-white/5">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[#FFD700]" /> PERFORMANCE RANKINGS
                </h3>
              </div>
              <ScrollArea className="h-[600px]">
                <div className="p-4 space-y-3">
                  {intel?.employees?.map((emp, idx) => {
                    const isSelected = selectedEmployee?.employee_id === emp.employee_id;
                    const medals = ['#FFD700', '#C0C0C0', '#CD7F32'];
                    const medalIcons = [<Crown key="1" className="w-5 h-5" />, <Medal key="2" className="w-5 h-5" />, <Star key="3" className="w-5 h-5" />];
                    
                    return (
                      <motion.div
                        key={emp.employee_id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.03 * idx }}
                        whileHover={{ x: 5 }}
                        onClick={() => handleSelectEmployee(emp)}
                        className={`p-4 rounded-xl cursor-pointer transition-all ${
                          isSelected 
                            ? 'bg-gradient-to-r from-[#9945FF]/20 to-[#00F5FF]/20 border border-[#9945FF]/50' 
                            : 'bg-white/5 border border-transparent hover:border-white/10'
                        }`}
                        data-testid={`employee-${emp.employee_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="relative">
                            <div 
                              className="w-12 h-12 rounded-xl flex items-center justify-center font-black text-lg"
                              style={{ 
                                backgroundColor: idx < 3 ? `${medals[idx]}20` : 'rgba(255,255,255,0.05)',
                                color: idx < 3 ? medals[idx] : '#666'
                              }}
                            >
                              {idx < 3 ? medalIcons[idx] : `#${emp.rank}`}
                            </div>
                            {emp.shifts?.active_shift && (
                              <motion.span 
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{ repeat: Infinity, duration: 2 }}
                                className="absolute -top-1 -right-1 w-4 h-4 bg-[#00FF88] rounded-full border-2 border-[#0d0d15]" 
                              />
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-bold text-white truncate">{emp.employee_name}</p>
                              <TierBadge tier={emp.performance.tier} />
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-gray-400">{emp.transactions.total_count} battles</span>
                              <span className="text-[#00FF88]">{formatCurrency(emp.transactions.total_deposits)}</span>
                              <span className="text-gray-500">{emp.shifts.total_hours.toFixed(1)}h</span>
                            </div>
                          </div>

                          <div className="text-right">
                            <p className="text-2xl font-black" style={{ color: emp.performance.tier_color }}>
                              {emp.performance.score}
                            </p>
                            <p className="text-xs text-gray-500">SCORE</p>
                          </div>

                          <ChevronRight className="w-5 h-5 text-gray-600" />
                        </div>

                        {/* Score Breakdown */}
                        <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-4 gap-2">
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Volume</p>
                            <Progress value={emp.performance.volume_score} className="h-1.5 mt-1" />
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Efficiency</p>
                            <Progress value={emp.performance.efficiency_score} className="h-1.5 mt-1" />
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Response</p>
                            <Progress value={emp.performance.response_score} className="h-1.5 mt-1" />
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-gray-500">Speed</p>
                            <Progress value={emp.performance.time_score} className="h-1.5 mt-1" />
                          </div>
                        </div>

                        {/* Recommendations Preview */}
                        {emp.recommendations?.length > 0 && (
                          <div className="mt-3 flex gap-2 flex-wrap">
                            {emp.recommendations.slice(0, 2).map((rec, i) => (
                              <span key={i} className={`text-xs px-2 py-1 rounded-lg ${
                                rec.priority === 'high' ? 'bg-[#FF3366]/10 text-[#FF3366]' :
                                rec.priority === 'medium' ? 'bg-[#FFD700]/10 text-[#FFD700]' :
                                'bg-white/5 text-gray-400'
                              }`}>
                                {rec.message.slice(0, 40)}...
                              </span>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </ScrollArea>
            </Card>
          </motion.div>

          {/* Employee Detail Panel */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.45 }}>
            <Card className="bg-[#0d0d15] border-0 overflow-hidden sticky top-4">
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#00F5FF] to-transparent" />
              
              {selectedEmployee && employeeDetail ? (
                <div className="p-4">
                  {/* Header */}
                  <div className="flex items-center gap-4 mb-6">
                    <Avatar className="w-16 h-16 border-2" style={{ borderColor: employeeDetail.performance.tier_color }}>
                      <AvatarFallback 
                        className="text-xl font-bold text-white"
                        style={{ background: `linear-gradient(135deg, ${employeeDetail.performance.tier_color}40, transparent)` }}
                      >
                        {employeeDetail.employee_name?.slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white">{employeeDetail.employee_name}</h3>
                      <p className="text-sm text-gray-400">{employeeDetail.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <TierBadge tier={employeeDetail.performance.tier} size="lg" />
                        <span className="text-sm font-mono text-gray-500">Rank #{employeeDetail.rank}</span>
                      </div>
                    </div>
                  </div>

                  {/* Performance Score */}
                  <div className="mb-6 p-4 rounded-xl bg-white/5 text-center">
                    <p className="text-5xl font-black" style={{ color: employeeDetail.performance.tier_color }}>
                      {employeeDetail.performance.score}
                    </p>
                    <p className="text-gray-400 text-sm">PERFORMANCE SCORE</p>
                    <div className="grid grid-cols-4 gap-2 mt-4">
                      <div>
                        <p className="text-xs text-gray-500">VOL</p>
                        <p className="font-bold text-white">{employeeDetail.performance.volume_score}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">EFF</p>
                        <p className="font-bold text-white">{employeeDetail.performance.efficiency_score}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">RSP</p>
                        <p className="font-bold text-white">{employeeDetail.performance.response_score}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">SPD</p>
                        <p className="font-bold text-white">{employeeDetail.performance.time_score}</p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="p-3 rounded-xl bg-[#00FF88]/10 border border-[#00FF88]/20">
                      <p className="text-xs text-gray-400">Total Deposits</p>
                      <p className="text-lg font-bold text-[#00FF88]">{formatCurrency(employeeDetail.transactions.total_deposits)}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-[#00F5FF]/10 border border-[#00F5FF]/20">
                      <p className="text-xs text-gray-400">Hours Worked</p>
                      <p className="text-lg font-bold text-[#00F5FF]">{employeeDetail.shifts.total_hours.toFixed(1)}h</p>
                    </div>
                    <div className="p-3 rounded-xl bg-[#FFD700]/10 border border-[#FFD700]/20">
                      <p className="text-xs text-gray-400">Efficiency</p>
                      <p className="text-lg font-bold text-[#FFD700]">{employeeDetail.transactions.efficiency}/hr</p>
                    </div>
                    <div className="p-3 rounded-xl bg-[#9945FF]/10 border border-[#9945FF]/20">
                      <p className="text-xs text-gray-400">Response Rate</p>
                      <p className="text-lg font-bold text-[#9945FF]">{employeeDetail.activity.response_rate}%</p>
                    </div>
                  </div>

                  {/* Best Hours */}
                  {employeeDetail.transactions.best_hours?.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-bold text-gray-400 mb-2 flex items-center gap-2">
                        <Flame className="w-4 h-4 text-[#FF6B00]" /> BEST HOURS
                      </h4>
                      <div className="flex gap-2">
                        {employeeDetail.transactions.best_hours.map((h, i) => (
                          <div key={i} className="flex-1 p-2 rounded-lg bg-white/5 text-center">
                            <p className="font-bold text-white">{formatHour(h.hour)}</p>
                            <p className="text-xs text-gray-500">{h.transactions} txns</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Activity Checks */}
                  <div className="mb-6">
                    <h4 className="text-sm font-bold text-gray-400 mb-2 flex items-center gap-2">
                      <Bell className="w-4 h-4 text-[#9945FF]" /> ACTIVITY CHECKS
                    </h4>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 rounded-lg bg-[#00FF88]/10">
                        <CheckCircle className="w-4 h-4 mx-auto text-[#00FF88] mb-1" />
                        <p className="text-lg font-bold text-white">{employeeDetail.activity.responded}</p>
                        <p className="text-xs text-gray-500">Responded</p>
                      </div>
                      <div className="p-2 rounded-lg bg-[#FFD700]/10">
                        <Timer className="w-4 h-4 mx-auto text-[#FFD700] mb-1" />
                        <p className="text-lg font-bold text-white">{employeeDetail.activity.pending}</p>
                        <p className="text-xs text-gray-500">Pending</p>
                      </div>
                      <div className="p-2 rounded-lg bg-[#FF3366]/10">
                        <XCircle className="w-4 h-4 mx-auto text-[#FF3366] mb-1" />
                        <p className="text-lg font-bold text-white">{employeeDetail.activity.missed}</p>
                        <p className="text-xs text-gray-500">Missed</p>
                      </div>
                    </div>
                    {employeeDetail.activity.avg_response_time > 0 && (
                      <p className="text-xs text-gray-500 text-center mt-2">
                        Avg response: {employeeDetail.activity.avg_response_time.toFixed(1)}s
                      </p>
                    )}
                  </div>

                  {/* Recent Shifts */}
                  <div className="mb-6">
                    <h4 className="text-sm font-bold text-gray-400 mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#00F5FF]" /> RECENT SHIFTS
                    </h4>
                    <ScrollArea className="h-[150px]">
                      <div className="space-y-2">
                        {employeeDetail.shifts.recent_shifts?.map((shift, i) => (
                          <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-white/5">
                            <div>
                              <p className="text-sm text-white">{formatDateTime(shift.clock_in)}</p>
                              <p className="text-xs text-gray-500">
                                {shift.clock_out ? `Out: ${formatDateTime(shift.clock_out)}` : 'Active'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-mono text-[#00F5FF]">{shift.hours.toFixed(1)}h</p>
                              <Badge className={`text-xs ${shift.status === 'active' ? 'bg-[#00FF88]/10 text-[#00FF88]' : 'bg-white/10 text-gray-400'}`}>
                                {shift.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                        {(!employeeDetail.shifts.recent_shifts || employeeDetail.shifts.recent_shifts.length === 0) && (
                          <p className="text-center text-gray-500 py-4">No shift data</p>
                        )}
                      </div>
                    </ScrollArea>
                  </div>

                  {/* Recommendations */}
                  {employeeDetail.recommendations?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold text-gray-400 mb-2 flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-[#FFD700]" /> IMPROVEMENT TIPS
                      </h4>
                      <div className="space-y-2">
                        {employeeDetail.recommendations.map((rec, i) => (
                          <div 
                            key={i} 
                            className={`p-3 rounded-lg border ${
                              rec.priority === 'high' ? 'bg-[#FF3366]/10 border-[#FF3366]/30' :
                              rec.priority === 'medium' ? 'bg-[#FFD700]/10 border-[#FFD700]/30' :
                              'bg-white/5 border-white/10'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <AlertTriangle className={`w-4 h-4 ${
                                rec.priority === 'high' ? 'text-[#FF3366]' :
                                rec.priority === 'medium' ? 'text-[#FFD700]' :
                                'text-gray-400'
                              }`} />
                              <span className="text-xs uppercase text-gray-500">{rec.priority}</span>
                            </div>
                            <p className="text-sm text-white">{rec.message}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : loadingDetail ? (
                <div className="p-8 text-center">
                  <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1 }} className="mx-auto w-10 h-10 rounded-full border-2 border-[#9945FF]/30 border-t-[#9945FF]" />
                  <p className="text-gray-500 mt-4">Loading intel...</p>
                </div>
              ) : (
                <div className="p-8 text-center">
                  <motion.div 
                    animate={{ 
                      boxShadow: ['0 0 30px #9945FF20', '0 0 60px #9945FF40', '0 0 30px #9945FF20'],
                    }}
                    transition={{ repeat: Infinity, duration: 3 }}
                    className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#9945FF]/10 to-[#00F5FF]/10 border border-[#9945FF]/20 flex items-center justify-center mx-auto mb-4"
                  >
                    <Eye className="w-10 h-10 text-[#9945FF]" />
                  </motion.div>
                  <h3 className="text-lg font-bold text-white mb-2">SELECT AGENT</h3>
                  <p className="text-sm text-gray-500">Click on an employee to view detailed intel</p>
                </div>
              )}
            </Card>
          </motion.div>
        </div>
      </main>

      {/* Peak Hour Detail Modal */}
      <AnimatePresence>
        {showPeakHourDetail && peakHourDetail && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowPeakHourDetail(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg"
            >
              <Card className="bg-[#0d0d15] border border-[#FF6B00]/30 overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF6B00] to-transparent" />
                
                {/* Header */}
                <div className="p-4 border-b border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-xl bg-[#FF6B00]/20">
                      <Flame className="w-6 h-6 text-[#FF6B00]" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Peak Hour Details</h2>
                      <p className="text-sm text-gray-400">{formatHour(peakHourDetail.hour)} on {peakHourDetail.date}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowPeakHourDetail(false)}
                    className="hover:bg-white/5"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                {/* Stats */}
                <div className="p-4 grid grid-cols-3 gap-4">
                  <div className="p-3 rounded-xl bg-white/5 text-center">
                    <p className="text-xs text-gray-400">Transactions</p>
                    <p className="text-2xl font-black text-white">{peakHourDetail.transactions_count}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-white/5 text-center">
                    <p className="text-xs text-gray-400">Deposits</p>
                    <p className="text-xl font-bold text-[#00FF88]">{formatCurrency(peakHourDetail.total_deposits)}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-white/5 text-center">
                    <p className="text-xs text-gray-400">Withdrawals</p>
                    <p className="text-xl font-bold text-[#FF3366]">{formatCurrency(peakHourDetail.total_withdrawals)}</p>
                  </div>
                </div>

                {/* Active Staff */}
                <div className="p-4 border-t border-white/5">
                  <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#00F5FF]" />
                    Active Staff ({peakHourDetail.employees_active?.length || 0})
                  </h3>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {peakHourDetail.employees_active?.length > 0 ? (
                      peakHourDetail.employees_active.map((emp, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-white/5">
                          <div className="flex items-center gap-3">
                            <Avatar className="w-8 h-8">
                              <AvatarFallback className="bg-[#9945FF]/20 text-[#9945FF] font-bold text-xs">
                                {emp.username?.slice(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium text-white">{emp.username}</span>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-mono text-[#FFD700]">{formatCurrency(emp.total_processed)}</p>
                            <p className="text-xs text-gray-400">{emp.transactions_count} txns</p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-gray-500 py-4">No staff data available</p>
                    )}
                  </div>
                </div>

                {/* Top Transaction */}
                {peakHourDetail.top_transaction?.amount && (
                  <div className="p-4 border-t border-white/5">
                    <h3 className="font-bold text-white mb-2 flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-[#FFD700]" />
                      Top Transaction
                    </h3>
                    <div className="p-3 rounded-xl bg-gradient-to-r from-[#FFD700]/10 to-[#FF6B00]/10 border border-[#FFD700]/30">
                      <p className="text-2xl font-black text-[#FFD700]">{formatCurrency(peakHourDetail.top_transaction.amount)}</p>
                      <p className="text-sm text-gray-400">{peakHourDetail.top_transaction.type} - {peakHourDetail.top_transaction.player_name}</p>
                    </div>
                  </div>
                )}

                {/* Why Peak */}
                <div className="p-4 border-t border-white/5 bg-white/2">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-[#FFD700] mt-0.5" />
                    <div>
                      <p className="font-bold text-white">Why was this a peak hour?</p>
                      <p className="text-sm text-gray-400 mt-1">
                        {peakHourDetail.transactions_count > 10 
                          ? `High transaction volume with ${peakHourDetail.employees_active?.length || 0} active staff members processing ${peakHourDetail.transactions_count} transactions totaling ${formatCurrency(peakHourDetail.total_deposits + peakHourDetail.total_withdrawals)}.`
                          : `Moderate activity with ${peakHourDetail.employees_active?.length || 0} staff members on shift.`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
